import { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { useAppSelector } from '@store/hooks'
import type { DashboardPeriodParams } from '@services/api/dashboard/finance.api'
import {
  CALENDAR_DATE_FORMAT,
  rangeToPeriod,
} from '@utils/dashboardFinance.util'
import {
  buildDashboardPeriodCacheKey,
  clearDashboardPeriodInflight,
  DASHBOARD_PERIOD_CACHE_STALE_MS,
  getDashboardPeriodInflight,
  joinDashboardPeriodInflight,
  leaveDashboardPeriodInflight,
  readDashboardPeriodCache,
  resolveDashboardPeriodFetcherKey,
  setDashboardPeriodInflight,
  writeDashboardPeriodCache,
} from '@utils/dashboardPeriodCache.util'
import { useDashboardRefreshKey } from '@context/DashboardRefreshContext'

export function useDashboardClinicId(): string | null {
  return (
    useAppSelector((s) => s.clinic.selectedClinic?.clinic_id) ??
    localStorage.getItem('clinic_id')
  )
}

/** True while login/bootstrap has not yet resolved a clinic id. */
export function useDashboardClinicBootstrapPending(): boolean {
  const clinicId = useDashboardClinicId()
  const clinicLoading = useAppSelector((s) => s.clinic.loading)
  const hasClinics = useAppSelector((s) => s.clinic.data.length > 0)
  const clinicError = useAppSelector((s) => s.clinic.error)
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated)

  if (clinicId) return false
  if (clinicLoading) return true
  if (isAuthenticated && !hasClinics && !clinicError) return true
  return false
}

/** Date-only YYYY-MM-DD so API period matches detail-link calendar day (no UTC shift). */
function calendarDateToDateOnly(date: string): string {
  const parsed = moment(date, CALENDAR_DATE_FORMAT, true)
  return (parsed.isValid() ? parsed : moment()).format('YYYY-MM-DD')
}

export function useDashboardPeriodParams(
  range: RangeKey,
  date: string,
): DashboardPeriodParams | null {
  const clinicId = useDashboardClinicId()
  return useMemo((): DashboardPeriodParams | null => {
    if (!clinicId) return null
    return {
      clinicId,
      date: calendarDateToDateOnly(date),
      period: rangeToPeriod(range),
    }
  }, [clinicId, date, range])
}

export type DashboardPeriodFetchState<T> = {
  data: T | null
  /** Params for which `data` is valid; null when data is cleared. */
  dataParams: DashboardPeriodParams | null
  loading: boolean
  error: string | null
}

export type DashboardFetcherOptions = {
  signal?: AbortSignal
}

export type DashboardPeriodFetcher<T> = (
  // Period params may include endpoint-specific fields (e.g. operatoryCount).
  params: DashboardPeriodParams & Record<string, unknown>,
  options?: DashboardFetcherOptions,
) => Promise<T>

export type DashboardPeriodFetchOptions = {
  /** Bump to refetch without clearing current data (e.g. after appointment status change). */
  refreshKey?: number
  /** Skip network when false (hidden widgets or a later fetch wave). */
  enabled?: boolean
  /**
   * Visible section waiting for its wave. Keeps the card skeleton up without
   * starting the request or counting as in-flight work.
   */
  queued?: boolean
  /** Override cache key fragment; defaults to a WeakMap id for the fetcher. */
  cacheKey?: string
  /** Skip client cache (always network). */
  skipCache?: boolean
}

function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const maybe = error as { code?: string; name?: string; message?: string }
  return (
    maybe.code === 'ERR_CANCELED' ||
    maybe.name === 'CanceledError' ||
    maybe.name === 'AbortError' ||
    maybe.message === 'canceled' ||
    maybe.message === 'Cancelled'
  )
}

export function useDashboardPeriodFetch<T>(
  periodParams: DashboardPeriodParams | null,
  fetcher: DashboardPeriodFetcher<T>,
  options?: DashboardPeriodFetchOptions,
): DashboardPeriodFetchState<T> {
  const clinicId = useDashboardClinicId()
  const clinicBootstrapPending = useDashboardClinicBootstrapPending()
  const dashboardRefreshKey = useDashboardRefreshKey()
  const refreshKey = (options?.refreshKey ?? 0) + dashboardRefreshKey
  const enabled = options?.enabled !== false
  const queued = options?.queued === true && !enabled
  const skipCache = options?.skipCache === true
  const cacheKeyName = resolveDashboardPeriodFetcherKey(
    fetcher,
    options?.cacheKey,
  )
  const activeParams = enabled ? periodParams : null

  const [data, setData] = useState<T | null>(null)
  const [dataParams, setDataParams] = useState<DashboardPeriodParams | null>(
    null,
  )
  const [loading, setLoading] = useState(
    () => queued || activeParams != null || clinicBootstrapPending,
  )
  const [error, setError] = useState<string | null>(null)
  const prevPeriodParamsRef = useRef<DashboardPeriodParams | null>(null)
  const prevRefreshKeyRef = useRef(refreshKey)

  useEffect(() => {
    if (!activeParams) {
      prevPeriodParamsRef.current = null
      setData(null)
      setDataParams(null)
      if (queued) {
        setError(null)
        setLoading(true)
      } else if (clinicBootstrapPending && enabled) {
        setLoading(true)
        setError(null)
      } else if (!clinicId && enabled) {
        setError('No clinic selected')
        setLoading(false)
      } else {
        setError(null)
        setLoading(false)
      }
      return
    }

    let cancelled = false
    const controller = new AbortController()
    let ownedRequest: Promise<T> | null = null
    let joinedPromise: Promise<T> | null = null
    const periodChanged = prevPeriodParamsRef.current !== activeParams
    const refreshOnly =
      !periodChanged && prevRefreshKeyRef.current !== refreshKey
    prevPeriodParamsRef.current = activeParams
    prevRefreshKeyRef.current = refreshKey

    // Interval/manual refresh keeps prior data visible while refetching.
    if (periodChanged) {
      setData(null)
      setDataParams(null)
    }

    const cacheKey = buildDashboardPeriodCacheKey(cacheKeyName, activeParams)

    // Serve fresh cache on period/mount (not on forced refresh).
    if (!skipCache && !refreshOnly) {
      const cached = readDashboardPeriodCache<T>(cacheKey)
      if (cached !== undefined) {
        setData(cached)
        setDataParams(activeParams)
        setLoading(false)
        setError(null)
        return () => {
          cancelled = true
        }
      }
    }

    setLoading(true)
    setError(null)

    const startRequest = (): Promise<T> => {
      joinedPromise = null
      const request = fetcher(activeParams, { signal: controller.signal }).then(
        (result) => {
          if (!skipCache) {
            writeDashboardPeriodCache(
              cacheKey,
              result,
              DASHBOARD_PERIOD_CACHE_STALE_MS,
            )
          }
          return result
        },
      )
      ownedRequest = request
      // Keep a handler on the raw promise so abort never becomes unhandled
      // before the effect chain attaches (Strict Mode / CRA overlay).
      void request.catch((err: unknown) => {
        if (isAbortError(err)) {
          clearDashboardPeriodInflight(cacheKey, request)
        }
      })
      return skipCache ? request : setDashboardPeriodInflight(cacheKey, request)
    }

    const runFetch = (): Promise<T> => {
      if (!skipCache) {
        const existing = getDashboardPeriodInflight<T>(cacheKey)
        if (existing && joinDashboardPeriodInflight(cacheKey)) {
          joinedPromise = existing
          // Reuse in-flight work, but if it was aborted by another owner,
          // start a fresh request with this effect's signal.
          return existing.catch((err: unknown) => {
            if (isAbortError(err)) {
              clearDashboardPeriodInflight(cacheKey, existing)
              return startRequest()
            }
            throw err
          })
        }
      }
      return startRequest()
    }

    void runFetch()
      .then((result) => {
        if (cancelled) return
        setData(result)
        setDataParams(activeParams)
      })
      .catch((e: unknown) => {
        if (cancelled || isAbortError(e)) return
        setError(
          e instanceof Error ? e.message : 'Failed to load dashboard data',
        )
        if (!refreshOnly) {
          setData(null)
          setDataParams(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      if (ownedRequest) {
        const lastSubscriber = leaveDashboardPeriodInflight(
          cacheKey,
          ownedRequest,
        )
        if (lastSubscriber) {
          controller.abort()
        }
      } else if (joinedPromise) {
        // Joined callers must not abort the shared request.
        leaveDashboardPeriodInflight(cacheKey, joinedPromise)
      } else {
        controller.abort()
      }
    }
  }, [
    activeParams,
    clinicId,
    clinicBootstrapPending,
    enabled,
    queued,
    fetcher,
    refreshKey,
    cacheKeyName,
    skipCache,
  ])

  const waitingForClinic = !activeParams && clinicBootstrapPending && enabled
  return {
    data,
    dataParams,
    loading: loading || queued || waitingForClinic,
    error: waitingForClinic ? null : error,
  }
}
